import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "column no-wrap gap-12" }
const _hoisted_2 = ["src"]
const _hoisted_3 = ["aspect-ratio"]
const _hoisted_4 = { class: "row no-wrap flex-center gap-sm bg-off-white-10 rounded-12 q-pa-xs" }
const _hoisted_5 = { class: "row no-wrap items-center gap-12" }

import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import 'cropperjs'
import type CropperCanvas from '@cropper/element-canvas'
import type CropperImage from '@cropper/element-image'
import type CropperSelection from '@cropper/element-selection'
import type { Selection } from '@cropper/element-selection'

interface ITextObject {
  title: string
  description: string
  reset: string
  cancel: string
  crop: string
}

interface IProps {
  image: File | null
  /** NaN for a free ratio */
  aspectRatio: number
  /** Output size in px; the crop is scaled to fit inside it */
  width: number
  height: number
  /** A PNG keeps the image's transparency; a JPEG lays it on white */
  outputType?: 'image/jpeg' | 'image/png'
  /** JPEG only */
  quality?: number
  /** In bytes; a JPEG is compressed to fit it, a PNG (which has no quality setting) is scaled down instead */
  maxSize?: number | undefined
  text?: ITextObject
}

interface IEmits {
  (event: 'crop', data: File): void
}

const SELECTION_COVERAGE = 0.75
const PNG = 'image/png'
const JPEG = 'image/jpeg'


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'KCropper' },
  __name: 'KCropper',
  props: /*@__PURE__*/_mergeModels({
    image: {},
    aspectRatio: {},
    width: {},
    height: {},
    outputType: { default: 'image/jpeg' },
    quality: { default: 1 },
    maxSize: { default: undefined },
    text: { default: () => ({
    title: 'Crop your image',
    description: 'Adjust the size and the position of your image',
    reset: 'Reset',
    cancel: 'Cancel',
    crop: 'Crop',
  }) }
  }, {
    "modelValue": { type: Boolean, ...{ required: true } },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["crop"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {


const model = _useModel<boolean>(__props, "modelValue")
const props = __props
const emit = __emit

const imagePreview = ref('')
const isImageFitted = ref(false)
const cropperCanvasRef = ref<CropperCanvas | null>(null)
const cropperImageRef = ref<CropperImage | null>(null)
const cropperSelectionRef = ref<CropperSelection | null>(null)

// The fit places the image and selection in valid positions by construction, so both guards are skipped
// while it runs (they would also mis-measure during the popup's opening scale transition)
let isFitting = false
// Prevents a clamped move from re-entering the clamp through its own change event
let isClampingSelection = false

function setImagePreview(file: File | null) {
  if (imagePreview.value) URL.revokeObjectURL(imagePreview.value)
  imagePreview.value = file ? URL.createObjectURL(file) : ''
}

// The area the selection may cover: the part of the image that is visible inside the canvas
function getSelectionBounds(): Selection | null {
  const cropperCanvas = cropperCanvasRef.value
  const cropperImage = cropperImageRef.value
  if (!cropperCanvas || !cropperImage) return null

  const canvasRect = cropperCanvas.getBoundingClientRect()
  const imageRect = cropperImage.getBoundingClientRect()
  const x = Math.max(imageRect.left, canvasRect.left) - canvasRect.left
  const y = Math.max(imageRect.top, canvasRect.top) - canvasRect.top
  const right = Math.min(imageRect.right, canvasRect.right) - canvasRect.left
  const bottom = Math.min(imageRect.bottom, canvasRect.bottom) - canvasRect.top
  return { x, y, width: Math.max(right - x, 0), height: Math.max(bottom - y, 0) }
}

// Fits the image inside the canvas and centres the selection inside the image. Works from layout sizes
// (offsetWidth / natural size) instead of `$center()`, which measures with getBoundingClientRect and is
// therefore skewed while the popup's opening transition scales it, so the fit is correct from the first frame.
async function fitImageAndSelection() {
  const cropperCanvas = cropperCanvasRef.value
  const cropperImage = cropperImageRef.value
  const cropperSelection = cropperSelectionRef.value
  if (!cropperCanvas || !cropperImage || !cropperSelection) return

  const src = imagePreview.value
  let image: HTMLImageElement
  try {
    image = await cropperImage.$ready()
  } catch {
    return
  }
  // The popup was closed or the image replaced while it was loading
  if (cropperImageRef.value !== cropperImage || imagePreview.value !== src) return

  const { offsetWidth: canvasWidth, offsetHeight: canvasHeight } = cropperCanvas
  const { naturalWidth, naturalHeight } = image
  const scale = Math.min(canvasWidth / naturalWidth, canvasHeight / naturalHeight)
  // A free ratio (NaN) starts the selection at the image's own ratio
  const aspectRatio = props.aspectRatio > 0 ? props.aspectRatio : naturalWidth / naturalHeight
  const selectionWidth = Math.min(naturalWidth * scale, naturalHeight * scale * aspectRatio) * SELECTION_COVERAGE
  const selectionHeight = selectionWidth / aspectRatio

  isFitting = true
  // The image is laid out at its natural size in the canvas's top-left corner and transforms around its
  // centre, so translating by half the size difference centres it
  cropperImage.$setTransform(scale, 0, 0, scale, (canvasWidth - naturalWidth) / 2, (canvasHeight - naturalHeight) / 2)
  cropperSelection.$change(
    (canvasWidth - selectionWidth) / 2,
    (canvasHeight - selectionHeight) / 2,
    selectionWidth,
    selectionHeight,
  )
  isFitting = false
  isImageFitted.value = true
}

function onCropperImageChange(event: CustomEvent<Selection>) {
  const cropperSelection = cropperSelectionRef.value
  if (isFitting || !cropperSelection) return

  // The image can't be moved or zoomed out so far that it stops covering the selection
  if (!inSelection(cropperSelection as Selection, event.detail)) event.preventDefault()
}

function onCropperSelectionChange(event: CustomEvent<Selection>) {
  if (isFitting) return
  const cropperSelection = cropperSelectionRef.value
  const bounds = getSelectionBounds()
  if (!cropperSelection || !bounds) return

  const selection = event.detail
  if (inSelection(selection, bounds)) return
  event.preventDefault()

  // A move that overshoots slides the selection against the edge instead of stopping short of it
  const isMove = selection.width === cropperSelection.width && selection.height === cropperSelection.height
  if (!isMove || isClampingSelection) return

  const minX = Math.ceil(bounds.x)
  const minY = Math.ceil(bounds.y)
  const maxX = Math.floor(bounds.x + bounds.width) - selection.width
  const maxY = Math.floor(bounds.y + bounds.height) - selection.height
  if (minX > maxX || minY > maxY) return

  isClampingSelection = true
  cropperSelection.$change(clamp(selection.x, minX, maxX), clamp(selection.y, minY, maxY))
  isClampingSelection = false
}

function inSelection(selection: Selection, maxSelection: Selection) {
  return (
    selection.x >= maxSelection.x &&
    selection.y >= maxSelection.y &&
    selection.x + selection.width <= maxSelection.x + maxSelection.width &&
    selection.y + selection.height <= maxSelection.y + maxSelection.height
  )
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function zoomIn() {
  cropperImageRef.value?.$zoom(0.1)
}

function zoomOut() {
  cropperImageRef.value?.$zoom(-0.1)
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Blob conversion failed'))
        resolve(blob)
      },
      type,
      quality,
    )
  })
}

async function compressToMaxSize(canvas: HTMLCanvasElement, maxBytes: number): Promise<Blob> {
  let minQuality = 0.4
  let maxQuality = 1
  let bestBlob: Blob | null = null

  for (let i = 0; i < 7; i++) {
    const quality = (minQuality + maxQuality) / 2

    const blob = await canvasToBlob(canvas, JPEG, quality)

    if (blob.size > maxBytes) {
      // too big → lower quality
      maxQuality = quality
    } else {
      // good → try higher quality
      minQuality = quality
      bestBlob = blob
    }
  }

  // fallback if nothing matched
  if (!bestBlob) bestBlob = await canvasToBlob(canvas, JPEG, minQuality)

  return bestBlob
}

async function cropToJpeg(cropperSelection: CropperSelection): Promise<Blob> {
  const canvas = await cropperSelection.$toCanvas({
    width: props.width,
    height: props.height,
    // JPEG has no transparency, so transparent pixels (e.g. of a PNG) are laid on white instead of turning black
    beforeDraw: (context, { width, height }) => {
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
    },
  })
  return props.maxSize ? compressToMaxSize(canvas, props.maxSize) : canvasToBlob(canvas, JPEG, props.quality)
}

// PNG ignores quality, so it's fitted to maxSize by re-cropping at smaller dimensions. The file size follows the
// pixel count, so each pass scales both sides by the square root of the overshoot (with a little margin).
async function cropToPng(cropperSelection: CropperSelection): Promise<Blob> {
  let { width, height } = props
  let blob = await canvasToBlob(await cropperSelection.$toCanvas({ width, height }), PNG)

  for (let i = 0; props.maxSize && blob.size > props.maxSize && i < 5; i++) {
    const scale = Math.sqrt(props.maxSize / blob.size) * 0.95
    width = Math.max(Math.floor(width * scale), 1)
    height = Math.max(Math.floor(height * scale), 1)
    blob = await canvasToBlob(await cropperSelection.$toCanvas({ width, height }), PNG)
  }
  return blob
}

async function crop() {
  const cropperSelection = cropperSelectionRef.value
  if (!cropperSelection) return

  const isPng = props.outputType === PNG
  const blob = isPng ? await cropToPng(cropperSelection) : await cropToJpeg(cropperSelection)
  emit('crop', new File([blob], isPng ? 'image.png' : 'image.jpg', { type: blob.type }))
  model.value = false
}

// Runs when the popup content mounts (the ref is set) and when the image is replaced while it is open.
// The image gets a fresh object URL each time: for a URL the browser has already decoded, `$ready()`
// resolves before cropperjs's own load handling runs, and that handling would then override the fit.
watch(
  [cropperImageRef, () => props.image],
  async ([cropperImage]) => {
    isImageFitted.value = false
    setImagePreview(cropperImage ? props.image : null)
    if (!cropperImage) return

    await nextTick()
    fitImageAndSelection()
  },
  { flush: 'post' },
)

onBeforeUnmount(() => setImagePreview(null))

return (_ctx: any,_cache: any) => {
  const _component_KBtn = _resolveComponent("KBtn")!
  const _component_KPopup = _resolveComponent("KPopup")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_openBlock(), _createBlock(_component_KPopup, {
    modelValue: model.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((model).value = $event)),
    text: __props.text.title,
    description: __props.text.description,
    "max-width": "472px",
    class: "k-cropper"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("cropper-canvas", {
          ref_key: "cropperCanvasRef",
          ref: cropperCanvasRef,
          background: "",
          "theme-color": "color-mix(in srgb, var(--space-b-100) 50%, transparent)",
          class: _normalizeClass(["k-cropper__canvas", { 'k-cropper__canvas--pending': !isImageFitted.value }])
        }, [
          _createElementVNode("cropper-image", {
            ref_key: "cropperImageRef",
            ref: cropperImageRef,
            src: imagePreview.value,
            alt: "Picture",
            scalable: "",
            skewable: "",
            translatable: "",
            "initial-fit": "contain",
            onChange: onCropperImageChange
          }, null, 40, _hoisted_2),
          _cache[2] || (_cache[2] = _createElementVNode("cropper-shade", { hidden: "" }, null, -1)),
          _cache[3] || (_cache[3] = _createElementVNode("cropper-handle", {
            action: "select",
            plain: ""
          }, null, -1)),
          _cache[4] || (_cache[4] = _createElementVNode("cropper-handle", {
            action: "move",
            plain: ""
          }, null, -1)),
          _createElementVNode("cropper-selection", {
            ref_key: "cropperSelectionRef",
            ref: cropperSelectionRef,
            "initial-coverage": SELECTION_COVERAGE,
            movable: "",
            resizable: "",
            outlined: "",
            "aspect-ratio": __props.aspectRatio,
            onChange: onCropperSelectionChange
          }, [...(_cache[1] || (_cache[1] = [
            _createElementVNode("cropper-grid", {
              role: "grid",
              covered: "",
              "theme-color": "color-mix(in srgb, var(--cool-gray-50) 50%, transparent)"
            }, null, -1),
            _createElementVNode("cropper-crosshair", { centered: "" }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "move",
              "theme-color": "rgba(255, 255, 255, 0.0)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "n-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "e-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "s-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "w-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "ne-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "nw-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "se-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1),
            _createElementVNode("cropper-handle", {
              action: "sw-resize",
              "theme-color": "var(--space-b-100)"
            }, null, -1)
          ]))], 40, _hoisted_3)
        ], 2),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_KBtn, {
            model: "secondary",
            icon: "minus",
            size: "sm",
            onClick: zoomOut
          }),
          _createVNode(_component_KBtn, {
            model: "secondary",
            icon: "plus",
            size: "sm",
            onClick: zoomIn
          }),
          _createVNode(_component_KBtn, {
            model: "secondary",
            icon: "refresh",
            "is-right-icon": "",
            size: "sm",
            label: __props.text.reset,
            onClick: fitImageAndSelection
          }, null, 8, ["label"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _withDirectives(_createVNode(_component_KBtn, {
            model: "secondary",
            size: "md",
            label: __props.text.cancel,
            class: "flex-1"
          }, null, 8, ["label"]), [
            [_directive_close_popup]
          ]),
          _createVNode(_component_KBtn, {
            model: "primary",
            size: "md",
            label: __props.text.crop,
            class: "flex-1",
            onClick: crop
          }, null, 8, ["label"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "text", "description"]))
}
}

})